import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../lib/format";
import api from "../lib/api";
import CartCrossSell from "../components/CartCrossSell";

export default function Cart() {
  const { items, updateQty, remove } = useCart();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (items.length === 0) {
      setPreview(null);
      return;
    }
    setPreviewLoading(true);
    const payload = {
      items: items.map((i) => ({
        product_id: i.product_id,
        quantity: i.quantity,
        variant_id: i.variant_id || null,
      })),
    };
    api
      .post("/cart/preview", payload)
      .then((r) => setPreview(r.data))
      .catch(() => setPreview(null))
      .finally(() => setPreviewLoading(false));
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag size={32} strokeWidth={1.25} className="mx-auto mb-6 text-white/30" />
        <h1 className="font-display text-3xl font-bold uppercase tracking-tighter text-white">
          {t("cart.empty")}
        </h1>
        <p className="mt-3 text-sm text-white/60">{t("cart.empty_subtitle")}</p>
        <Link
          to="/shop"
          className="mt-8 inline-block border border-white bg-white text-[#001F3F] hover:bg-transparent hover:text-white transition-all uppercase tracking-[0.25em] text-xs font-bold px-8 py-4"
          data-testid="cart-empty-cta"
        >
          {t("cart.empty_cta")}
        </Link>
      </div>
    );
  }

  const subtotal = preview?.subtotal ?? items.reduce((s, i) => s + i.price * i.quantity, 0);
  const bundleDiscount = preview?.bundle_discount ?? 0;
  const bundleLabel = preview?.bundle_label || "";
  const shipping = preview?.shipping ?? (subtotal >= 75 ? 0 : 4.90);
  const total = preview?.total ?? subtotal + shipping;
  const nextTier = preview?.next_tier;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="cart-page">
      <div className="border-b border-white/10 pb-8 mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
          {t("nav.cart")}
        </div>
        <h1 className="font-display mt-3 text-4xl sm:text-5xl font-bold uppercase tracking-tighter text-white">
          {items.length} {items.length > 1 ? t("shop.items_plural") : t("shop.items_singular")}
        </h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-6">
          {nextTier && (
            <div
              className="border border-[#C5CDD8]/40 bg-gradient-to-r from-[#0A2A4F] to-[#001F3F] p-5 flex items-start gap-4"
              data-testid="bundle-upsell-banner"
            >
              <Sparkles size={20} strokeWidth={1.25} className="text-[#C5CDD8] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5CDD8]">
                  {t("cart.bonus_unlock")}
                </div>
                <p className="mt-2 text-sm text-white">
                  {t("cart.add_to_unlock", {
                    count: nextTier.items_needed,
                    discount: nextTier.to_unlock,
                  })}
                </p>
              </div>
            </div>
          )}

          {bundleLabel && (
            <div
              className="border border-[#A6E0B5]/30 bg-[#0A2A4F] p-5 flex items-start gap-3"
              data-testid="bundle-active-banner"
            >
              <Sparkles size={18} strokeWidth={1.5} className="text-[#A6E0B5] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-white">
                <strong>{bundleLabel}</strong> {t("cart.applied_auto")} —{" "}
                <span className="text-[#A6E0B5] font-bold">
                  −{formatPrice(bundleDiscount)}
                </span>{" "}
                {t("cart.on_your_cart")}.
              </div>
            </div>
          )}

          {items.map((item) => (
            <div
              key={item.product_id + (item.variant_id || "")}
              className="flex gap-4 sm:gap-6 pb-6 border-b border-white/10"
              data-testid={`cart-item-${item.product_id}`}
            >
              <Link to={`/product/${item.product_id}`} className="bg-[#0A2A4F] w-24 sm:w-32 aspect-square overflow-hidden flex-shrink-0 border border-white/5">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${item.product_id}`} className="font-medium text-white hover:underline">
                  {item.name}
                </Link>
                {item.variant_label && (
                  <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#C5CDD8]" data-testid={`cart-variant-${item.product_id}`}>
                    {item.variant_label}
                  </div>
                )}
                <div className="mt-1 text-sm font-bold text-white">{formatPrice(item.price)}</div>
                <div className="mt-4 flex items-center gap-4">
                  <div className="inline-flex items-center border border-white/40">
                    <button
                      onClick={() => updateQty(item.product_id, item.quantity - 1, item.variant_id)}
                      className="p-2 text-white hover:bg-white hover:text-[#001F3F] transition"
                      data-testid={`cart-minus-${item.product_id}`}
                    >
                      <Minus size={12} />
                    </button>
                    <span className="w-10 text-center text-sm font-bold text-white">{item.quantity}</span>
                    <button
                      onClick={() => updateQty(item.product_id, item.quantity + 1, item.variant_id)}
                      className="p-2 text-white hover:bg-white hover:text-[#001F3F] transition"
                      data-testid={`cart-plus-${item.product_id}`}
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                  <button
                    onClick={() => remove(item.product_id, item.variant_id)}
                    className="text-xs font-bold uppercase tracking-widest text-white/60 hover:text-white inline-flex items-center gap-1"
                    data-testid={`cart-remove-${item.product_id}`}
                  >
                    <X size={12} /> {t("cart.remove")}
                  </button>
                </div>
              </div>
              <div className="text-sm font-bold text-white whitespace-nowrap">
                {formatPrice(item.price * item.quantity)}
              </div>
            </div>
          ))}
        </div>

        <aside className="lg:col-span-1">
          <div className="border border-white/10 bg-[#0A2A4F] p-6 lg:p-8 sticky top-28">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">{t("cart.summary")}</div>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-white/60">{t("common.subtotal")}</span>
                <span className="font-bold text-white" data-testid="subtotal">{formatPrice(subtotal)}</span>
              </div>
              {bundleDiscount > 0 && (
                <div className="flex justify-between" data-testid="bundle-discount-line">
                  <span className="text-[#A6E0B5]">{bundleLabel}</span>
                  <span className="font-bold text-[#A6E0B5]">
                    −{formatPrice(bundleDiscount)}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-white/60">{t("common.shipping")}</span>
                <span className="font-bold text-white">
                  {shipping === 0 ? t("common.free") : formatPrice(shipping)}
                </span>
              </div>
              {preview && preview.to_free_shipping > 0 && (
                <div className="text-xs text-white/60">
                  {t("cart.to_free_shipping", { amount: formatPrice(preview.to_free_shipping) })}
                </div>
              )}
              <div className="border-t border-white/10 pt-3 flex justify-between">
                <span className="font-bold uppercase tracking-widest text-xs text-white">{t("common.total")}</span>
                <span className="font-display text-xl font-bold text-white" data-testid="total">
                  {formatPrice(total)}
                </span>
              </div>
              {previewLoading && (
                <div className="text-[10px] text-white/40 uppercase tracking-widest">
                  {t("common.loading")}
                </div>
              )}
            </div>
            <button
              onClick={() => navigate("/checkout")}
              className="mt-8 w-full border border-white bg-white text-[#001F3F] hover:bg-transparent hover:text-white transition-all uppercase tracking-[0.25em] text-xs font-bold py-4"
              data-testid="checkout-button"
            >
              {t("cart.checkout")}
            </button>
            <Link
              to="/shop"
              className="block mt-4 text-center text-xs font-bold uppercase tracking-widest text-white/60 hover:text-white"
            >
              {t("cart.continue_shopping")}
            </Link>
          </div>
        </aside>
      </div>

      <CartCrossSell cartItems={items} />
    </div>
  );
}
